import {Observable} from 'rxjs';
import {APIResponse, Filter, GeneralOption, SimpleUnit} from '@core/interfaces/system/system-common';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {DownloadURL} from '@core/interfaces/engin/load-forecast/load-forecast';
import {PmDataRequest} from '@core/interfaces/engin/program-management/pm-reporting';
import {ProjectAssetDto} from '@core/interfaces/engin/program-management/project';
import {
    SpatialCardMetricRequest,
    SpatialCardResponseMetric,
} from '@theme/components/card-components/spatial-card/model/model-spatial-card';

/*
 * Response AlternativeResponseDto
 */
export interface Alternative {
    id: number;
    createdOn: Date;
    updatedOn: Date;
    name: string;
    description: string;
    status: AlternativeStatusType;
    projectId: number;
    createdType: AlternativeSourceType;
    lastModifiedType: AlternativeSourceType;
    alternativeSelected: boolean;
}

export interface AlternativeDto extends Alternative {
    workflowItemId: number;
    assetCountTotal: number;
    assetCountPoint: number;
    assetCountLinear: number;
    assetLength: number;
    lengthUnit: SimpleUnit;
    totalCost: number;
    tcoTotal: number;
    tcoWithoutCost: number;
    benefitGross: number;
    benefitNet: number;
    ratioNetBenefitCost: number;
    countPastTul: number;
    countWorstHealth: number;
    riskCurrent: number;
    ciCurrent: number;
    chiCurrent: number;
    missingAlternativeOutcome: boolean;
}

export interface AlternativeFullDto extends AlternativeDto {
    interventions: InterventionSummaryItem[];
    outcomesComparison: MetricComparisonItem[];
}

export interface InterventionSummaryItem {
    label: string;
    countReplace: number;
    countRepair: number;
    countDoNothing: number;
    countInstall: number;
    countTotal: number;
    costTotal: number;
}

export interface MetricComparisonItem {
    outcomeType: AlternativeOutcomeType;
    label: string;
    unit: SimpleUnit;
    baseAlternativeValue: number;
    currentAlternativeValue: number;
    deltaValue: number;
    deltaUnit: SimpleUnit;
    deltaValueGood: boolean;
    skipDelta: boolean;
}

export interface AlternativeAssetDto extends ProjectAssetDto {
    alternativeId: number;
    interventionType: string;
    interventionYear: number;
    renewalParams: AssetRenewalParams;
    installParams: AssetInstallParams;
}

export interface AlternativeCostResponse {
    fixedList: AlternativeCostItem[];
    userList: AlternativeCostItem[];
    costUnit: SimpleUnit;
    // TODO: chart;
}

export interface AlternativeCostItem {
    parameterName: string;
    costValue: number;
}

export interface AlternativeMetricResponse {
    fixedList: AlternativeMetricItem[];
    userList: AlternativeMetricItem[];
    costUnit: SimpleUnit;
    // TODO: chart;
}

export interface AlternativeMetricItem {
    metricType: AlternativeMetricType;
    parameterName: string;
    metricUnit: SimpleUnit;
    valueBefore: number;
    valueAfter: number;
    valueDelta: number;
}

/*
 * Request
 */
export interface AlternativeCreateDto {
    name: string;
    description: string;
    projectId: number;
    scenarioKey: AlternativeScenarioType;
    workflowItemId: number;
}

export class AlternativeUpdateDto {
    name: string;
    description: string;

    static fromAlternative(a: AlternativeDto): AlternativeUpdateDto {
        return {
            name: a.name,
            description: a.description,
        } as AlternativeUpdateDto;
    }
}

export interface UpdateInterventionRequest extends PmDataRequest {
    existingAssetChanges: AlternativeAssetDto[];
    installAssets: AssetInstallParams[];
}

export interface ResetAlternativeRequest extends PmDataRequest {
    scenarioKey: AlternativeScenarioType;
}

export interface AlternativeInterventionDto {
    id: number;
    alternativeId: number;
    interventionType: string;
    assetId: string;
    interventionYear: number;
    interventionCost: number;
    renewalParams: AssetRenewalParams;
    installParams: AssetInstallParams;
}

export interface AssetRenewalParams {
    assetId: string;
}

export interface AssetInstallParams {
    assetClassCode: string;
    assetClass: string;
    relatedAssetId: string;
    relatedAssetClassCode: string;
    installType?: InstallAssetType;
}

export interface AlternativeScenarioOption extends GeneralOption {}

export interface AssetInterventionOption extends GeneralOption {
    applyToAllAssets?: boolean;
    applyForAssetClassCodes?: string[];
}

/*
 * Enums
 */
export enum AlternativeSourceType {
    USER = 'USER',
    SYSTEM = 'SYSTEM',
}

export enum AlternativeOutcomeType {
    TOTAL_ASSET_COUNT = 'TOTAL_ASSET_COUNT',
    SCOPE_ASSET_COUNT_TOTAL = 'SCOPE_ASSET_COUNT_TOTAL',
    SCOPE_ASSET_COUNT_POINT = 'SCOPE_ASSET_COUNT_POINT',
    SCOPE_ASSET_LENGTH = 'SCOPE_ASSET_LENGTH',
    TOTAL_COST = 'TOTAL_COST',
    BENEFIT_GROSS = 'BENEFIT_GROSS',
    BENEFIT_NET = 'BENEFIT_NET',
    RATIO_NET_BENEFIT_COST = 'RATIO_NET_BENEFIT_COST',
    FORECAST_CURRENT_RISK = 'FORECAST_CURRENT_RISK',
    FORECAST_CURRENT_CI = 'FORECAST_CURRENT_CI',
    FORECAST_CURRENT_CHI = 'FORECAST_CURRENT_CHI',
    COUNT_ASSET_PAST_TUL = 'COUNT_ASSET_PAST_TUL',
    COUNT_ASSET_WORST_HEALTH = 'COUNT_ASSET_WORST_HEALTH',
}

export enum AlternativeScenarioType {
    NONE = 'NONE',
    REPLACE_ALL_ECONOMIC_EOL = 'REPLACE_ALL_ECONOMIC_EOL',
    REPLACE_ALL_ECONOMIC_EOL_MORATORIUM_10_YEARS = 'REPLACE_ALL_ECONOMIC_EOL_MORATORIUM_10_YEARS',
    REPLACE_ALL_NOW = 'REPLACE_ALL_NOW',
}

export const AlternativeScenarioTypeLabel = {
    [AlternativeScenarioType.NONE]: 'None',
    [AlternativeScenarioType.REPLACE_ALL_ECONOMIC_EOL]: 'Replace at Economic EOL',
    [AlternativeScenarioType.REPLACE_ALL_ECONOMIC_EOL_MORATORIUM_10_YEARS]: 'Moratorium for: 10 years',
    [AlternativeScenarioType.REPLACE_ALL_NOW]: 'Replace all (now)',
};

export enum AssetInterventionType {
    NONE = 'NONE',
    REPLACE = 'REPLACE',
    REPAIR = 'REPAIR',
    INSTALL = 'INSTALL',
}

export enum AlternativeMetricType {
    CUSTOMER_INTERRUPTED = 'CUSTOMER_INTERRUPTED',
    CUSTOMER_HOURS_INTERRUPTED = 'CUSTOMER_HOURS_INTERRUPTED',
    RISK_TOTAL = 'RISK_TOTAL',
}

export const AlternativeMetricTypeLabel = {
    [AlternativeMetricType.CUSTOMER_INTERRUPTED]: 'CI',
    [AlternativeMetricType.CUSTOMER_HOURS_INTERRUPTED]: 'CHI',
    [AlternativeMetricType.RISK_TOTAL]: 'Total Risk',
};

export enum InstallAssetType {
    RECLOSER = 'RECLOSER',
    SWITCH = 'SWITCH',
}

export const InstallAssetTypeLabel = {
    [InstallAssetType.RECLOSER]: 'Recloser',
    [InstallAssetType.SWITCH]: 'Switch',
};

export enum AlternativeStatusType {
    CALCULATING = 'CALCULATING',
    READY = 'READY',
    FAILED = 'FAILED',
}

export const AlternativeStatusTypeLabel = {
    [AlternativeStatusType.CALCULATING]: 'Calculating',
    [AlternativeStatusType.READY]: 'Ready',
    [AlternativeStatusType.FAILED]: 'Failed',
};

export abstract class AlternativeService {
    /*
     * APIs for Alternatives model
     */
    abstract createAlternative(alternative: AlternativeCreateDto): Observable<APIResponse<Alternative>>;

    abstract editAlternativeDetails(
        alternativeId: number,
        alternative: AlternativeUpdateDto,
    ): Observable<APIResponse<Alternative>>;

    abstract deleteAlternative(alternativeId: number): Observable<APIResponse<boolean>>;

    abstract getAlternativeList(params?: Filter[], pageSize?: number): Observable<Alternative[]>;

    abstract updateAlternativeInterventions(
        alternativeId: number,
        updateDto: UpdateInterventionRequest,
    ): Observable<APIResponse<AlternativeFullDto>>;

    abstract resetAlternative(
        alternativeId: number,
        updateDto: ResetAlternativeRequest,
    ): Observable<APIResponse<AlternativeFullDto>>;

    abstract setSelectedAlternative(alternativeId: number): Observable<APIResponse<boolean>>;

    /*
     * APIs for AlternativeDto
     */
    abstract getAlternativeDto(alternativeId: number, request: PmDataRequest): Observable<APIResponse<AlternativeDto>>;

    abstract getAlternativeDtoList(
        request: PmDataRequest,
        params?: Filter[],
        pageSize?: number,
    ): Observable<AlternativeDto[]>;

    abstract getAlternativeDtoListCsv(request: PmDataRequest, params?: Filter[]): Observable<APIResponse<DownloadURL>>;

    /*
     * APIs for AlternativeFullDto
     */
    abstract getAlternativeDtoFull(
        alternativeId: number,
        request: PmDataRequest,
    ): Observable<APIResponse<AlternativeFullDto>>;

    abstract getAlternativeFullDtoListTable(request: PmDataRequest, params?: Filter[]): Observable<DataSource>;

    /*
     * APIs for AlternativeAssetDto
     */
    abstract getAlternativeAssetsList(
        alternativeId: number,
        request: PmDataRequest,
        filters?: Filter[],
        pageSize?: number,
    ): Observable<AlternativeAssetDto[]>;

    abstract getAlternativeAssetsListCsv(
        alternativeId: number,
        request: PmDataRequest,
        params?: string,
    ): Observable<APIResponse<DownloadURL>>;

    // response items: AlternativeAssetDto[]
    abstract getAlternativeAssetsListTable(alternativeId: number, request: PmDataRequest): Observable<DataSource>;

    /*
     * APIs related to analysis of costs and benefits
     */
    abstract getCostResults(
        alternativeId: number,
        request: PmDataRequest,
    ): Observable<APIResponse<AlternativeCostResponse>>;

    abstract getBenefitResultsByType(
        alternativeId: number,
        benefitType: AlternativeMetricType,
        request: PmDataRequest,
    ): Observable<APIResponse<AlternativeMetricResponse>>;

    /*
     * Other support APIs
     */
    abstract getGeospatialCardDataByAlternative(
        alternativeId: number,
        request: SpatialCardMetricRequest,
    ): Observable<APIResponse<SpatialCardResponseMetric>>;

    /*
     * Other support APIs
     */
    abstract getInterventionOptions(): Observable<APIResponse<AssetInterventionOption[]>>;

    abstract validateAlternativeName(projectId: number, alternativeName: string): Observable<boolean>;

    /*
     * Helpers - call other APIs
     */
    abstract getAlternativeDtoByProjectTable(projectId: number, request: PmDataRequest): Observable<DataSource>;

    abstract getBulkAlternativeDtoListCsv(
        request: PmDataRequest,
        paramsString: string,
    ): Observable<APIResponse<DownloadURL>>;
    abstract getBulkAlternativeAssetsListCsv(
        request: PmDataRequest,
        paramsString: string,
    ): Observable<APIResponse<DownloadURL>>;

    abstract recalculateAlternatives(workflowItemId: number, projectId: number): Observable<APIResponse<boolean>>;
}
